// ============================================================
// math_utils.js
// EV Powertrain Simulator — core 모듈
// 수학 유틸리티 — 보간, 통계, 수치해석
// Extracted from ev-sim_v1_0_Verification_46.jsx (Phase 2)
// [Fix-C1] 로컬 const g 제거 → G_GRAVITY SST import로 교체
// ============================================================

import { G_GRAVITY } from '../../constants/physics_consts.js';

// ── lerp1D ──
/**
 * 1차원 선형 보간 (Lookup Table 기반)
 *
 * xPts 배열에서 x 위치를 찾아 좌우 두 점으로 선형 보간한다.
 * x가 범위를 벗어나면 양 끝 값으로 클램프(외삽 없음).
 *
 * @param {number[]} xPts  - 입력 브레이크포인트 배열 (오름차순)
 * @param {number[]} yPts  - 출력 값 배열 (xPts와 동일 길이)
 * @param {number}   x     - 보간할 입력 값
 * @returns {number}       - 보간된 출력 값
 */
function lerp1D(xPts, yPts, x) {
  if (!xPts || !yPts || xPts.length < 2) return 0;
  const xc = Math.max(xPts[0], Math.min(xPts[xPts.length - 1], x));
  for (let i = 0; i < xPts.length - 1; i++) {
    if (xc >= xPts[i] && xc <= xPts[i + 1]) {
      const t = (xc - xPts[i]) / (xPts[i + 1] - xPts[i]);
      return yPts[i] * (1 - t) + yPts[i + 1] * t;
    }
  }
  return yPts[yPts.length - 1];
}

// ── bilinear2D ──
/**
 * 2차원 쌍선형 보간 (2D LUT)
 *
 * 2D 격자(행=rowPts, 열=colPts)에서 (rowVal, colVal) 위치의 값을
 * 주변 4점을 이용한 쌍선형 보간으로 산출한다.
 * 범위 벗어난 입력은 경계 값으로 클램프(외삽 없음).
 *
 * @param {number[][]} lut    - 2D 룩업 테이블 [행][열]
 * @param {number[]}   rowPts - 행 브레이크포인트 배열
 * @param {number[]}   colPts - 열 브레이크포인트 배열
 * @param {number}     rowVal - 보간할 행 값
 * @param {number}     colVal - 보간할 열 값
 * @returns {number}          - 보간된 출력 값
 */
function bilinear2D(lut, rowPts, colPts, rowVal, colVal) {
  if (!lut || lut.length < 2) return 0;
  const rc = Math.max(rowPts[0], Math.min(rowPts[rowPts.length - 1], rowVal));
  const cc = Math.max(colPts[0], Math.min(colPts[colPts.length - 1], colVal));
  let ri = 0, ci = 0;
  for (let i = 0; i < rowPts.length - 1; i++) { if (rc >= rowPts[i] && rc <= rowPts[i + 1]) { ri = i; break; } }
  for (let j = 0; j < colPts.length - 1; j++) { if (cc >= colPts[j] && cc <= colPts[j + 1]) { ci = j; break; } }
  const tr = (rc - rowPts[ri]) / Math.max(1e-9, rowPts[ri + 1] - rowPts[ri]);
  const tc = (cc - colPts[ci]) / Math.max(1e-9, colPts[ci + 1] - colPts[ci]);
  const r0 = lut[ri] || [], r1 = lut[ri + 1] || [];
  const q00 = (r0[ci]   != null) ? r0[ci]   : 0;
  const q01 = (r0[ci+1] != null) ? r0[ci+1] : q00;
  const q10 = (r1[ci]   != null) ? r1[ci]   : q00;
  const q11 = (r1[ci+1] != null) ? r1[ci+1] : q01;
  return q00*(1-tr)*(1-tc) + q01*(1-tr)*tc + q10*tr*(1-tc) + q11*tr*tc;
}

// ── lerpLUT ──
/**
 * 단일 구간 선형 보간 — lerp1D와 유사하나 빈 배열·단일 원소 방어 코드 포함
 *
 * lerp1D와 달리 xArr.length===1 케이스와 빈 배열을 안전하게 처리한다.
 * 성능이 중요한 내부 배터리/모터 LUT 조회에 주로 사용된다.
 *
 * @param {number[]} xArr - 입력 브레이크포인트 배열 (오름차순)
 * @param {number[]} yArr - 출력 값 배열
 * @param {number}   x    - 보간할 입력 값
 * @returns {number}      - 보간된 출력 값 (범위 초과 시 끝 값 반환)
 */
function lerpLUT(xArr, yArr, x) {
  // [Fix-LUT-Guard] 빈 배열 또는 단일 원소 입력 시 NaN/TypeError 방지
  if (!xArr || !yArr || xArr.length === 0 || yArr.length === 0) return 0;
  if (xArr.length === 1) return yArr[0] ?? 0;
  const s = Math.max(xArr[0], Math.min(xArr[xArr.length - 1], x));
  if (s <= xArr[0]) return yArr[0];
  if (s >= xArr[xArr.length - 1]) return yArr[yArr.length - 1];
  for (let i = 0; i < xArr.length - 1; i++) {
    if (s >= xArr[i] && s <= xArr[i + 1]) {
      const t = (s - xArr[i]) / (xArr[i + 1] - xArr[i]);
      return yArr[i] + t * (yArr[i + 1] - yArr[i]);
    }
  }
  // [Fix-MU1] 클램프(s ∈ [xArr[0], xArr[N-1]])로 이 분기는 도달 불가(dead code).
  // [Fix-③] 정적 분석 경고 제거를 위해 명시적 return 추가.
  return yArr[yArr.length - 1];
}

// ── derateScale ──
/**
 * 온도 기반 선형 디레이팅 계수 산출
 *
 * T ≤ T_start 이면 1.0(정격), T ≥ T_end 이면 0.0(완전 차단),
 * 그 사이에서 선형 감소: scale = 1 − (T − T_start) / (T_end − T_start)
 *
 * @param {number} T       - 현재 온도 [°C]
 * @param {number} T_start - 디레이팅 시작 온도 [°C]
 * @param {number} T_end   - 완전 차단(0.0) 온도 [°C]
 * @returns {number}       - 디레이팅 계수 [0, 1]
 */
function derateScale(T, T_start, T_end) {
  if (T <= T_start) return 1.0;
  if (T >= T_end) return 0.0;
  return 1.0 - (T - T_start) / (T_end - T_start);
}

// ── rhoCorrected ──
/**
 * 온도·고도 보정 공기 밀도 산출
 *
 * 기온 T_amb [°C]와 고도 altitude [m]를 반영한 실제 공기 밀도를 계산한다.
 *   ρ(T) = 1.225 × (288.15 / T_K)          [kg/m³] — 온도 보정 (등압 이상기체)
 *   ρ(T, h) = ρ(T) × exp(−g·h / (R·T_K))   [kg/m³] — 고도 보정 (등온 기압 감소 근사)
 *
 * 표준 조건(15°C, 0m)에서 ρ = 1.225 kg/m³.
 *
 * @param {number} T_amb    - 외기 온도 [°C]
 * @param {number} altitude - 고도 [m]
 * @returns {number}        - 보정된 공기 밀도 [kg/m³]
 */
function rhoCorrected(T_amb, altitude) {
  const T_K = T_amb + 273.15;
  const rho_temp = 1.225 * (288.15 / T_K);
  return rho_temp * Math.exp(-G_GRAVITY * altitude / (287.05 * T_K));
}

// ── effectiveMass ──
/**
 * 회전 관성을 포함한 등가 질량 산출
 *
 * 구동계 회전체(휠, 드라이브샤프트, 모터 로터)의 관성 모멘트를
 * 병진 운동 등가 질량으로 환산하여 차체 질량에 더한다.
 *   m_eff = m + (I_wheel×4 + I_ds×GR² + I_motor×GR²) / r_wheel²
 *
 * params.rotational_inertia_enable = false 이면 차체 질량만 반환.
 *
 * @param {object} params
 *   params.m                      - 차체 질량 [kg]
 *   params.rotational_inertia_enable - 회전 관성 반영 여부
 *   params.I_wheel                - 단일 휠 관성 모멘트 [kg·m²] (기본 1.5)
 *   params.I_driveshaft           - 드라이브샤프트 관성 [kg·m²] (기본 0.08)
 *   params.J_motor                - 모터 로터 관성 [kg·m²] (기본 0.05)
 *   params.gear_ratio             - 기어비 (무차원)
 *   params.r_wheel                - 타이어 유효 반경 [m]
 * @returns {number}               - 등가 질량 [kg]
 */
function effectiveMass(params) {
  if (!params.rotational_inertia_enable) return params.m;
  const I_w = (params.I_wheel || 1.5) * 4; // 4 wheels
  const I_ds = (params.I_driveshaft || 0.08) * (params.gear_ratio * params.gear_ratio);
  // Motor rotor inertia referred to wheel
  const I_motor_ref = (params.J_motor || 0.05) * (params.gear_ratio * params.gear_ratio);
  return params.m + (I_w + I_ds + I_motor_ref) / (params.r_wheel * params.r_wheel);
}

// ── doe.js 전용 통계·GP 헬퍼 ──
// [Fix-⑤] 함수 본문을 _math_internal.js로 이전 — math_utils public API 오염 해소.
//   globalThis 경유 호출(zero-import 설계) 하위호환을 위해 re-export 유지.
import {
  _erfApprox,
  _normCDF,
  _normPDF,
  _rbf,
  _matSolve,
  lgamma,
  incompleteBeta,
  fDistPValue,
} from './_math_internal.js';

// ── odeStepRK4 ── (고전 4차 룽게-쿠타 단일 스텝)
/**
 * 4차 Runge-Kutta (RK4) ODE 단일 스텝
 *
 * dy/dt = f(t, y)  →  y_{n+1} = y_n + (k1 + 2k2 + 2k3 + k4) / 6
 *
 * 오일러 1차 대비 지역 오차 O(dt⁵), 전역 오차 O(dt⁴).
 * 단일 스텝이므로 호출자가 dt를 직접 관리한다.
 * 적응형 스텝이 필요하면 odeStepRK45 + adaptOdeDt 를 사용.
 *
 * @param {function} f      - f(t, y) → dy/dt, y는 숫자 또는 Float64Array
 * @param {number}   t      - 현재 시각 [s]
 * @param {number|Float64Array} y - 현재 상태
 * @param {number}   dt     - 시간 스텝 [s]
 * @returns {number|Float64Array} 다음 상태 y_{n+1}
 *
 * 참고: Butcher (1964), Hairer et al. "Solving ODEs I" §II.1
 */
function odeStepRK4(f, t, y, dt) {
  const isScalar = typeof y === 'number';
  const add  = isScalar
    ? (a, b, s) => a + s * b
    : (a, b, s) => a.map((ai, i) => ai + s * b[i]);
  const scale = isScalar
    ? (a, s) => s * a
    : (a, s) => a.map(ai => s * ai);
  const addAll = isScalar
    ? (k1, k2, k3, k4) => (k1 + 2 * k2 + 2 * k3 + k4) / 6
    : (k1, k2, k3, k4) => k1.map((_, i) => (k1[i] + 2*k2[i] + 2*k3[i] + k4[i]) / 6);

  const k1 = f(t,          y);
  const k2 = f(t + dt/2,   add(y, k1, dt/2));
  const k3 = f(t + dt/2,   add(y, k2, dt/2));
  const k4 = f(t + dt,     add(y, k3, dt));
  return add(y, addAll(k1, k2, k3, k4), dt);
}

// ── odeStepRK45 ── (Dormand-Prince RK45, 오차 추정 포함)
/**
 * Dormand-Prince RK45 단일 스텝 — 4차+5차 동시 산출로 오차 추정
 *
 * 내장 Butcher 표 (DOPRI5):
 *   5차 해로 전진, 4차 해와의 차이로 지역 오차 e = |y5 − y4| 추정.
 *   adaptOdeDt() 와 결합해 스텝 크기를 자동 조절한다.
 *
 * @param {function} f  - f(t, y) → dy/dt
 * @param {number}   t
 * @param {number|Float64Array} y
 * @param {number}   dt
 * @returns {{ y5, y4, err_norm, t_new }}
 *   y5       : 5차 해 (전진 사용)
 *   y4       : 4차 해 (오차 추정용)
 *   err_norm : 정규화 오차 (atol/rtol 적용 전 상대값, 스칼라)
 *   t_new    : t + dt
 *
 * 참고: Dormand & Prince (1980); Hairer et al. §II.4
 */
function odeStepRK45(f, t, y, dt) {
  // Dormand-Prince Butcher 표 계수
  const c2=1/5, c3=3/10, c4=4/5, c5=8/9;
  const a21=1/5;
  const a31=3/40,    a32=9/40;
  const a41=44/45,   a42=-56/15,  a43=32/9;
  const a51=19372/6561, a52=-25360/2187, a53=64448/6561, a54=-212/729;
  const a61=9017/3168,  a62=-355/33,    a63=46732/5247, a64=49/176, a65=-5103/18656;
  // 5차 가중치
  const e1=35/384, e3=500/1113, e4=125/192, e5=-2187/6784, e6=11/84;
  // 오차 계수 (5차 − 4차)
  const d1=71/57600, d3=-71/16695, d4=71/1920, d5=-17253/339200, d6=22/525, d7=-1/40;

  const isArr = !( typeof y === 'number' );
  const vec = isArr
    ? (a, bs) => a.map((ai,i) => ai + bs.reduce((s,[k,c])=>s+c*k[i],0))
    : (a, bs) => a + bs.reduce((s,[k,c])=>s+c*k,0);

  const k1 = f(t,           y);
  const k2 = f(t+c2*dt,     vec(y, [[k1,a21*dt]]));
  const k3 = f(t+c3*dt,     vec(y, [[k1,a31*dt],[k2,a32*dt]]));
  const k4 = f(t+c4*dt,     vec(y, [[k1,a41*dt],[k2,a42*dt],[k3,a43*dt]]));
  const k5 = f(t+c5*dt,     vec(y, [[k1,a51*dt],[k2,a52*dt],[k3,a53*dt],[k4,a54*dt]]));
  const k6 = f(t+dt,        vec(y, [[k1,a61*dt],[k2,a62*dt],[k3,a63*dt],[k4,a64*dt],[k5,a65*dt]]));
  const y5 = vec(y, [[k1,e1*dt],[k3,e3*dt],[k4,e4*dt],[k5,e5*dt],[k6,e6*dt]]);
  const k7 = f(t+dt, y5); // FSAL: 다음 스텝의 k1로 재사용 가능
  // 오차 벡터 (5차 − 4차 내재적)
  const errVec = isArr
    ? y5.map((_,i)=> dt*(d1*k1[i]+d3*k3[i]+d4*k4[i]+d5*k5[i]+d6*k6[i]+d7*k7[i]))
    : dt*(d1*k1+d3*k3+d4*k4+d5*k5+d6*k6+d7*k7);
  // RMS 정규화 오차 (스케일은 호출자에서 atol/rtol 적용)
  const errNorm = isArr
    ? Math.sqrt(errVec.reduce((s,e)=>s+e*e,0)/errVec.length)
    : Math.abs(errVec);
  return { y5, y4: vec(y,[[k1,e1*dt],[k3,e3*dt],[k4,e4*dt],[k5,e5*dt],[k6,e6*dt]]), err_norm: errNorm, t_new: t+dt, k7 };
}

// ── adaptOdeDt ── (PI 제어기 기반 스텝 크기 자동 조절)
/**
 * Hairer-Norsett-Wanner PI 제어기 기반 적응형 dt 결정
 *
 * 안전 계수 0.9, PI 지수 α=0.7/ord, β=0.4/ord (ord=5, DOPRI5 기준).
 * 허용 오차 ε = atol + rtol × |y|  (IEEE 754 혼합 기준).
 *
 * @param {number} err_norm  - odeStepRK45.err_norm (정규화 오차)
 * @param {number} err_prev  - 이전 스텝 정규화 오차 (PI 이력)
 * @param {number} dt        - 현재 스텝 크기 [s]
 * @param {number} atol      - 절대 허용 오차 (기본 1e-6)
 * @param {number} rtol      - 상대 허용 오차 (기본 1e-4)
 * @param {number} dt_min    - 최소 스텝 [s] (기본 1e-6)
 * @param {number} dt_max    - 최대 스텝 [s] (기본 0.1)
 * @returns {{ dt_new, accept }}
 *   dt_new : 다음 스텝 제안값
 *   accept : 현재 스텝 수락 여부 (false면 재시도)
 *
 * 참고: Hairer et al. §II.4 "Automatic Step Size Control"
 */
function adaptOdeDt(err_norm, err_prev, dt, atol=1e-6, rtol=1e-4, dt_min=1e-6, dt_max=0.1) {
  const ord  = 5;
  const tol  = atol + rtol; // 단순 혼합 (스케일된 err_norm 가정)
  const q    = err_norm / tol;
  const accept = q <= 1.0;
  // PI 제어기: factor = 0.9 × q^(-α) × q_prev^(β)
  const alpha  = 0.7 / ord;
  const beta   = 0.4 / ord;
  const q_prev = Math.max(err_prev / tol, 1e-10);
  const factor = Math.min(5.0, Math.max(0.2,
    0.9 * Math.pow(q, -alpha) * Math.pow(q_prev, beta)
  ));
  const dt_new = Math.min(dt_max, Math.max(dt_min, dt * factor));
  return { dt_new, accept };
}

// ── checkAndAdjustDt ── (CFL 조건 기반 자동 dt 클램프)
/**
 * CFL 조건 기반 안정 dt 상한 산출 및 자동 조정
 *
 * 집중 열 모델(τ = C/ΣUA)·전기화학(τ = RC) 등 1차 ODE의 Euler 안정 조건:
 *   dt_stable ≤ 2 × τ_min  (Euler, 보수적 계수 적용 시 0.5 × τ_min)
 *
 * @param {number} dt           - 요청 스텝 [s]
 * @param {number} tau_min      - 시스템 최소 시정수 [s]
 * @param {number} [cfl=0.5]    - CFL 계수 (기본 0.5, Euler 안정)
 * @param {number} [dt_min=1e-5]- 하한 (시정수가 극단적으로 작을 때 방어)
 * @returns {{ dt_adj, clamped, dt_stable }}
 *   dt_adj   : 조정된 스텝
 *   clamped  : 클램프 발생 여부
 *   dt_stable: 이론 안정 상한
 */
function checkAndAdjustDt(dt, tau_min, cfl = 0.5, dt_min = 1e-5) {
  const dt_stable = cfl * Math.max(tau_min, dt_min / cfl);
  const clamped   = dt > dt_stable;
  return { dt_adj: clamped ? dt_stable : dt, clamped, dt_stable };
}


/**
 * Van der Corput 저불일치 수열 생성
 *
 * 지정 기저(base)의 반데르코르퓻 수열을 생성한다.
 * Halton 수열의 단일 차원 버전으로, 각 요소 ∈ [0, 1) 범위에서
 * 균등 분포에 가까운 준무작위(quasi-random) 수열을 반환한다.
 *
 * @param {number} n    - 샘플 수
 * @param {number} base - 기저 소수 (예: 2, 3, 5 …)
 * @returns {number[]}  - n 길이의 [0,1) 수열
 */
function vanDerCorput(n, base) {
  const seq = new Array(n);
  for (let i = 0; i < n; i++) {
    let q = 0, b = 1, num = i + 1;
    while (num > 0) { b /= base; q += (num % base) * b; num = Math.floor(num / base); }
    seq[i] = q;
  }
  return seq;
}

// ── haltonSequence (구 sobolSequence) ──
/**
 * [Fix-MU2] 명칭 수정: 이 구현은 소수 기저 반데르코르퓻(Van der Corput) 수열을
 * 다차원으로 확장한 Halton 수열이며, 방향 벡터 기반의 진짜 Sobol 수열이 아님.
 * 하위 호환성을 위해 `sobolSequence` 이름도 계속 export하되, 신규 코드에서는
 * `haltonSequence` 사용을 권장.
 *
 * @param {number} n  샘플 수
 * @param {number} k  차원 수 (최대 15 — 내장 소수 개수)
 * @returns {number[][]}  n×k 배열, 각 원소 ∈ [0, 1)
 */
function haltonSequence(n, k, offset = 0) {  // [B-6 Fix] offset: B행렬을 위한 시퀀스 오프셋
  const primes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47];
  return Array.from({ length: n }, (_, i) => {
    return Array.from({ length: k }, (_, j) => {
      const base = primes[j % primes.length];
      let q = 0, b = 1, num = i + 1 + offset;
      while (num > 0) { b /= base; q += (num % base) * b; num = Math.floor(num / base); }
      return q;
    });
  });
}
/** @deprecated haltonSequence 를 사용하세요 */
const sobolSequence = haltonSequence;

// ── randn ──
/**
 * 표준 정규 분포 난수 생성 (Box-Muller 변환)
 *
 * 균등 분포 U(0,1) 두 개를 이용해 N(0,1) 표준 정규 분포 샘플을 생성한다.
 *   z = √(−2·ln u) · cos(2π·v),  u, v ~ U(0,1), u ≠ 0
 *
 * @returns {number} - 표준 정규 분포 N(0,1) 샘플
 */
function randn() {
  let u, v;
  do { u = Math.random(); v = Math.random(); } while (u === 0);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

export {
  // ── Public API ──
  lerp1D,
  bilinear2D,
  lerpLUT,
  derateScale,
  rhoCorrected,
  effectiveMass,
  odeStepRK4,          // ✅ RevB: 4차 RK 적분기
  odeStepRK45,         // ✅ RevB: Dormand-Prince RK45 (오차 추정 포함)
  adaptOdeDt,          // ✅ RevB: PI 제어 적응형 스텝 조절
  checkAndAdjustDt,    // ✅ RevB: CFL 자동 dt 클램프
  vanDerCorput,
  sobolSequence,   // @deprecated — haltonSequence alias
  haltonSequence,
  randn,
  // ── doe.js 전용 re-export (_math_internal.js 출처) ──
  // [Fix-⑤] 본문은 _math_internal.js로 이전. globalThis 경유 접근(zero-import) 하위호환 유지.
  // 신규 코드는 _math_internal.js에서 직접 import할 것.
  _erfApprox,
  _normCDF,
  _normPDF,
  _rbf,
  _matSolve,
  incompleteBeta,
  lgamma,
  fDistPValue,
};